"use client";

/**
 * The 16:9 frame every story film plays in (the Why BB story and the role
 * films). Full screen fills the screen and, on a phone held upright, turns the
 * film sideways (Gordon 2026-11-03): real full screen + landscape lock where
 * the browser allows it (Android, desktop); elsewhere (iPhone has no element
 * full screen) a layer over the whole app that rotates 90° itself. A phone
 * turned on its side fills the screen by itself. Controls step aside after
 * 2 s without a touch or a mouse move. Keys: space play/pause, ←/→, F.
 *
 * The children are drawn inside a container sized by its own width, so cqw
 * units keep the film 16:9 on any screen.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronLeft, ChevronRight, Maximize, Minimize, Pause, Play, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

export function StoryFrame({ kiosk = false, label, playing, onTogglePlay, onPrev, onNext, onRestart, children }: {
  /** /screens: no controls, no keys, no auto full screen. */
  kiosk?: boolean;
  label: string;
  playing: boolean;
  onTogglePlay: () => void;
  onPrev: () => void;
  onNext: () => void;
  onRestart: () => void;
  children: React.ReactNode;
}) {
  const t = useTranslations("whyStory");
  const tCommon = useTranslations("common");
  const stageRef = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(false);
  const [pseudo, setPseudo] = useState(false);
  const [view, setView] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const on = () => {
      const isFull = document.fullscreenElement === stageRef.current;
      setFull(isFull);
      if (!isFull) { try { (screen.orientation as unknown as { unlock?: () => void })?.unlock?.(); } catch { /* not supported */ } }
    };
    const size = () => setView({ w: window.innerWidth, h: window.innerHeight });
    size();
    document.addEventListener("fullscreenchange", on);
    window.addEventListener("resize", size);
    return () => { document.removeEventListener("fullscreenchange", on); window.removeEventListener("resize", size); };
  }, []);
  const expanded = full || pseudo;

  const [idle, setIdle] = useState(false);
  useEffect(() => {
    const el = stageRef.current;
    if (!el || kiosk) return;
    let timer = 0;
    const wake = () => { setIdle(false); window.clearTimeout(timer); timer = window.setTimeout(() => setIdle(true), 2000); };
    wake();
    el.addEventListener("pointermove", wake);
    el.addEventListener("pointerdown", wake);
    el.addEventListener("touchstart", wake, { passive: true });
    window.addEventListener("keydown", wake);
    return () => {
      window.clearTimeout(timer);
      el.removeEventListener("pointermove", wake);
      el.removeEventListener("pointerdown", wake);
      el.removeEventListener("touchstart", wake);
      window.removeEventListener("keydown", wake);
    };
  }, [kiosk]);
  const turned = expanded && view.h > view.w;

  // Only on a change of orientation, so closing it sideways sticks.
  const lastSideways = useRef<boolean | null>(null);
  const autoFull = useRef(false);
  useEffect(() => {
    if (kiosk || !view.w) return;
    const phone = window.matchMedia?.("(pointer: coarse)").matches && Math.min(view.w, view.h) < 600;
    const sideways = view.w > view.h;
    const changed = lastSideways.current !== null && lastSideways.current !== sideways;
    lastSideways.current = sideways;
    if (!phone || !changed || full) return;
    if (sideways && !pseudo) { autoFull.current = true; setPseudo(true); }
    else if (!sideways && pseudo && autoFull.current) { autoFull.current = false; setPseudo(false); }
  }, [view.w, view.h, kiosk, full, pseudo]);

  const toggleFull = useCallback(async () => {
    if (document.fullscreenElement) { await document.exitFullscreen().catch(() => {}); return; }
    if (pseudo) { setPseudo(false); return; }
    const el = stageRef.current;
    if (el?.requestFullscreen) {
      try {
        await el.requestFullscreen({ navigationUI: "hide" });
        try { await (screen.orientation as unknown as { lock?: (o: string) => Promise<void> })?.lock?.("landscape"); } catch { /* the film turns itself instead */ }
        return;
      } catch { /* fall through to the layer */ }
    }
    setPseudo(true);
  }, [pseudo]);
  useEffect(() => {
    if (!pseudo) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setPseudo(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [pseudo]);

  const actions = useRef({ onTogglePlay, onPrev, onNext, toggleFull });
  actions.current = { onTogglePlay, onPrev, onNext, toggleFull };
  useEffect(() => {
    if (kiosk) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable) return;
      if (e.key === " ") { e.preventDefault(); actions.current.onTogglePlay(); }
      else if (e.key === "ArrowRight") actions.current.onNext();
      else if (e.key === "ArrowLeft") actions.current.onPrev();
      else if (e.key === "f" || e.key === "F") void actions.current.toggleFull();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [kiosk]);

  return (
    <div
      ref={stageRef}
      className={cn(
        "relative w-full overflow-hidden select-none bg-[#171512] text-[#f5f1ea]",
        expanded && idle && "cursor-none",
        pseudo ? "fixed inset-0 z-[101] flex h-[100dvh] w-screen items-center justify-center"
          : full ? "h-full flex items-center justify-center" : kiosk ? "h-full" : "aspect-video rounded-[var(--thumbnail-radius)]",
      )}
      role="region"
      aria-roledescription={t("aria")}
      aria-label={label}
    >
      <div
        className={cn("relative shrink-0 [container-type:inline-size]", turned ? "" : expanded ? "aspect-video w-full max-h-full max-w-[calc(100dvh*16/9)]" : "h-full w-full")}
        style={turned ? (() => { const w = Math.min(view.h, (view.w * 16) / 9); return { width: w, height: (w * 9) / 16, transform: "rotate(90deg)" }; })() : undefined}
      >
        {children}

        {!kiosk && (
          <div className={cn("absolute right-[1.4cqw] top-[1.4cqw] z-10 flex items-center gap-[0.6cqw] transition-opacity duration-300 focus-within:pointer-events-auto focus-within:opacity-100", idle ? "pointer-events-none opacity-0" : "opacity-100")}>
            {[
              { label: t("previous"), icon: ChevronLeft, on: onPrev },
              { label: playing ? tCommon("pause") : tCommon("play"), icon: playing ? Pause : Play, on: onTogglePlay, pressed: playing },
              { label: t("next"), icon: ChevronRight, on: onNext },
              { label: t("restart"), icon: RotateCcw, on: onRestart },
              { label: expanded ? t("exitFullscreen") : t("fullscreen"), icon: expanded ? Minimize : Maximize, on: () => void toggleFull() },
            ].map(({ label: l, icon: Icon, on, pressed }) => (
              <button
                key={l}
                type="button"
                onClick={on}
                aria-label={l}
                title={l}
                aria-pressed={pressed}
                className="grid h-[max(2.6cqw,32px)] w-[max(2.6cqw,32px)] place-items-center rounded-full bg-black/55 text-white ring-1 ring-white/50 transition-colors hover:bg-white hover:text-black"
              >
                <Icon className="h-[55%] w-[55%]" aria-hidden />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
